import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import type { TagEntry } from '@/features/gate/commands'
import {
  Btn,
  Cta,
  Footer,
  ItemRow,
  Keypad,
  Readout,
  Row,
  SheetActions,
  Strip,
  V1Screen,
  V1Sheet,
  WaitCta,
  Warn,
} from '@/features/gate/v1/kit'
import { C, typeKey } from '@/features/gate/v1/style'
import { kg, shortCode, tr } from '@/features/gate/v1/text'
import { DoneScreen } from '@/features/store/v1/bits'
import type { CloseIssuePayload, IssueToRunPayload } from '../commands'
import '../commands'
import type { BagView, IssueTaskView, ProcessState } from '../projection'
import { checkIssueBag } from '../model/rules'
import {
  Grid2,
  Hint,
  LeadBox,
  NextArrow,
  PHeader,
  SackChip,
  SavedLine,
  SaveError,
  saveFailed,
  stepLabel,
  SumCard,
  TagChip,
  tail2,
} from './ui'
import {
  PScanner,
  refusalText,
  ScalePad,
  useScaleEntry,
  WithSite,
  type ScaleReading,
} from './parts'

/**
 * F6.10 — रन को बोरी देना (V1): the store hands an issue task's bags to the run, weighing each bag
 * once (and the lot's moisture once). Same rules and issue_to_run / close_issue commands as the
 * full IssuePage, on the boards' look: the task, scan a bag, its weight, its moisture, done.
 */
export function V1Issue() {
  const navigate = useNavigate()
  const [taskId, setTaskId] = useState<string | null>(null)
  const [closed, setClosed] = useState<string | null>(null)
  const actor = useActor()
  // A store keeper comes here from the godown home; machine people from the machine board.
  const homePath = hasAnyRole(actor.roles, [ROLES.MACHINE_OPERATOR, ROLES.SUPERVISOR])
    ? '/m/process'
    : '/m/store'
  const home = () => navigate(homePath)
  return (
    <WithSite>
      {(state) => {
        if (closed)
          return (
            <DoneScreen
              title={tr('process.v1.issue.doneTitle')}
              lines={tr('process.v1.issue.doneLine', { run: tail2(closed) })}
              chips={[]}
              onHome={home}
            />
          )
        const task = taskId ? state.issueTasks.get(taskId) : undefined
        return task ? (
          <IssueTask
            state={state}
            task={task}
            onBack={() => setTaskId(null)}
            onClosed={() => {
              setClosed(task.run_code)
              setTaskId(null)
            }}
          />
        ) : (
          <TaskList state={state} onPick={setTaskId} onHome={home} />
        )
      }}
    </WithSite>
  )
}

const lotCodes = (state: ProcessState, task: IssueTaskView) =>
  task.unit_ids.map((u) => tail2(state.units.get(u)?.code ?? u)).join(', ')

function TaskList({
  state,
  onPick,
  onHome,
}: {
  state: ProcessState
  onPick: (id: string) => void
  onHome: () => void
}) {
  const tasks = [...state.issueTasks.values()].filter(
    (x) => x.status === 'LOADING' && !x.closed_local,
  )
  return (
    <V1Screen
      header={<PHeader onBack={onHome} icon="sack" title={tr('process.v1.issue.title')} />}
      footer={
        <Footer>
          {tasks.length ? (
            <WaitCta>{tr('process.v1.issue.pick')}</WaitCta>
          ) : (
            <Cta label={tr('process.v1.issue.home')} icon="home" lead onClick={onHome} />
          )}
        </Footer>
      }
    >
      {tasks.length === 0 ? (
        <>
          <Strip icon="ok" iconBox>
            {tr('process.v1.issue.none')}
          </Strip>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.4, color: C.muted }}>
            {tr('process.v1.issue.noneBody')}
          </p>
        </>
      ) : (
        tasks.map((task) => (
          <ItemRow
            key={task.shipment_id}
            padding="8px 10px"
            onClick={() => onPick(task.shipment_id)}
            lead={<LeadBox icon="sack" />}
            title={tr('process.v1.issue.lots', { lots: lotCodes(state, task) })}
            line={
              <span style={{ color: C.muted }}>
                {tr('process.v1.issue.toRun', {
                  run: tail2(task.run_code),
                  step: task.step_code ? stepLabel(state, task.step_code) : '—',
                })}
                <br />
                <b style={{ color: C.ink }}>
                  {tr('process.v1.issue.progress', {
                    n: task.issued.size,
                    m: task.container_ids.length,
                  })}
                </b>
              </span>
            }
            right={<NextArrow />}
          />
        ))
      )}
    </V1Screen>
  )
}

interface Pending {
  bag: BagView
  qr: string
  entry: TagEntry
}

function IssueTask({
  state,
  task,
  onBack,
  onClosed,
}: {
  state: ProcessState
  task: IssueTaskView
  onBack: () => void
  onClosed: () => void
}) {
  const actor = useActor()
  const [pending, setPending] = useState<Pending | null>(null)
  const [reading, setReading] = useState<ScaleReading | null>(null)
  const [moisture, setMoisture] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState<{ tag: string; net_g: number } | null>(null)
  const [confirmDone, setConfirmDone] = useState(false)
  const pendingTare = pending?.bag.tare_g ?? 0
  const scale = useScaleEntry(state, pendingTare)
  const onMoistureKey = useCallback((key: string) => {
    setMoisture((c) => typeKey(c, key, { decimals: 1, max: 3 }))
  }, [])

  const step = task.step_code ? state.steps.get(task.step_code) : undefined
  const needsMoisture = !!step?.needs_moisture
  const issuedG = [...task.issued.values()].reduce<number>((a, g) => a + (g ?? 0), 0)
  const missing = task.container_ids.filter((c) => !task.issued.has(c))
  const issuedUnits = new Set(
    [...task.issued.keys()].map((c) => state.bags.get(c)?.unit_id).filter((x): x is string => !!x),
  )
  const moistureMissing =
    needsMoisture && [...issuedUnits].some((u) => !task.moisture_unit_ids.has(u))
  const sackName = pending?.bag.sack_type_id
    ? state.pack.sack_types.find((s) => s.id === pending.bag.sack_type_id)?.name
    : null
  const askMoisture = !!pending && !task.moisture_unit_ids.has(pending.bag.unit_id)
  const m = /^(\d{1,2})(?:\.(\d))?$/.exec(moisture.trim())
  const moistureValue = m ? `${m[1]}.${m[2] ?? '0'}` : null
  const moistureOk = !askMoisture || !needsMoisture || moistureValue !== null
  const parentIds = task.sync.pending_command_id ? [task.sync.pending_command_id] : []

  const clearBag = () => {
    setPending(null)
    setReading(null)
    setMoisture('')
    setErr(null)
    scale.reset()
  }

  const saveBag = async (r: ScaleReading) => {
    if (!pending || r.read_g <= pendingTare || !moistureOk) return
    setSaving(true)
    setErr(null)
    try {
      const item_id = newId()
      const payload: IssueToRunPayload = {
        item_id,
        shipment_id: task.shipment_id,
        run_id: task.run_id,
        container_id: pending.bag.container_id,
        qr_payload: pending.qr,
        entry: pending.entry,
        weight_read_g: r.read_g,
        tare_g: pendingTare,
        confirmed_twice: r.confirmed_twice,
        moisture_pct: askMoisture ? moistureValue : null,
      }
      await enqueueCommand({
        id: item_id,
        type: 'issue_to_run',
        payload,
        actorId: actor.user_id,
        parentIds,
      })
      setSaved({ tag: pending.qr, net_g: r.read_g - pendingTare })
      clearBag()
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  const done = async () => {
    setSaving(true)
    setErr(null)
    try {
      const payload: CloseIssuePayload = {
        shipment_id: task.shipment_id,
        bags_done: task.issued.size,
        missing_container_ids: missing,
      }
      await enqueueCommand({
        type: 'close_issue',
        payload,
        actorId: actor.user_id,
        parentIds,
      })
      onClosed()
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
      setConfirmDone(false)
    }
  }

  const header = (title: string, back: () => void, icon: 'sack' | 'scale' | 'probe' = 'sack') => (
    <PHeader onBack={back} icon={icon} title={title} />
  )

  // F6.10-c — the lot's moisture, once (after the weight)
  if (pending && reading)
    return (
      <V1Screen
        main={{ gap: 8 }}
        header={header(tr('process.v1.issue.moistureTitle'), () => setReading(null), 'probe')}
        footer={
          <Footer>
            <SaveError text={err} />
            {moistureOk ? (
              <Cta
                label={tr('process.v1.issue.save')}
                icon="ok"
                busy={saving}
                onClick={() => void saveBag(reading)}
              />
            ) : (
              <WaitCta>{tr('process.v1.issue.moistureWait')}</WaitCta>
            )}
          </Footer>
        }
      >
        <SumCard
          rows={[
            {
              label: tr('process.v1.issue.bagTag'),
              value: <b>{tr('process.v1.tag', { no: shortCode(pending.qr) })}</b>,
            },
            {
              label: tr('process.v1.issue.netShort'),
              value: <b>{tr('process.v1.kg', { kg: kg(reading.read_g - pendingTare) })}</b>,
            },
          ]}
        />
        <Hint icon="probe">
          {needsMoisture
            ? tr('process.v1.issue.moistureNeeded')
            : tr('process.v1.issue.moistureOnce')}
        </Hint>
        <Readout
          label={tr('process.v1.issue.moisture')}
          value={moisture}
          unit="%"
          padding="6px 12px 8px"
          labelSize={16}
        />
        <div style={{ flexGrow: 1 }} />
        <Keypad onKey={onMoistureKey} />
      </V1Screen>
    )

  // F6.10-b — the bag on the scale
  if (pending) {
    const next = () => {
      const r = scale.submit()
      if (!r) return
      if (askMoisture) setReading(r)
      else void saveBag(r)
    }
    return (
      <V1Screen
        main={{ gap: 8 }}
        header={header(tr('process.v1.issue.weighTitle'), clearBag, 'scale')}
        footer={
          <Footer>
            <SaveError text={err} />
            {scale.grams > 0 ? (
              <Cta
                label={tr(askMoisture ? 'process.v1.next' : 'process.v1.issue.save')}
                icon={askMoisture ? 'next' : 'ok'}
                busy={saving}
                onClick={next}
              />
            ) : (
              <WaitCta>{tr('process.v1.issue.weightWait')}</WaitCta>
            )}
          </Footer>
        }
      >
        <Grid2>
          <TagChip code={pending.qr} onBad={clearBag} />
          <SackChip name={sackName ?? '—'} tare={kg(pendingTare)} />
        </Grid2>
        <Strip icon="scale">
          {tr('process.v1.issue.weightHint', {
            lot: tail2(state.units.get(pending.bag.unit_id)?.code ?? '—'),
          })}
        </Strip>
        <ScalePad label={tr('process.v1.issue.weight')} entry={scale} />
      </V1Screen>
    )
  }

  // F6.10-a — the task: scan the next bag, or done
  return (
    <V1Screen
      header={header(tr('process.v1.issue.title'), onBack)}
      footer={
        <Footer>
          <Row>
            <Btn icon="back" onClick={onBack}>
              {tr('process.v1.issue.otherTask')}
            </Btn>
          </Row>
          <SaveError text={confirmDone ? null : err} />
          {task.issued.size > 0 && !moistureMissing ? (
            <Cta
              label={tr('process.v1.issue.done')}
              icon="ok"
              busy={saving}
              onClick={() => (missing.length ? setConfirmDone(true) : void done())}
            />
          ) : (
            <WaitCta>
              {moistureMissing
                ? tr('process.v1.issue.moistureMissing')
                : tr('process.v1.issue.scanFirst')}
            </WaitCta>
          )}
        </Footer>
      }
      overlay={
        confirmDone ? (
          <V1Sheet
            title={tr('process.v1.issue.shortTitle')}
            onClose={() => setConfirmDone(false)}
            top={260}
            actions={
              <SheetActions>
                <Btn icon="back" onClick={() => setConfirmDone(false)}>
                  {tr('gate.v1.back')}
                </Btn>
                <Cta
                  label={tr('process.v1.issue.done')}
                  icon="ok"
                  busy={saving}
                  onClick={() => void done()}
                />
              </SheetActions>
            }
          >
            <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4 }}>
              {tr('process.v1.issue.shortConfirm', { n: missing.length })}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {missing.map((c) => (
                <span
                  key={c}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    fontSize: 17,
                    fontWeight: 700,
                  }}
                >
                  {tr('process.v1.tag', { no: shortCode(state.bags.get(c)?.qr_payload ?? c) })}
                </span>
              ))}
            </div>
            <SaveError text={err} />
          </V1Sheet>
        ) : undefined
      }
    >
      {saved && (
        <SavedLine>
          {tr('process.v1.issue.saved', { no: shortCode(saved.tag), kg: kg(saved.net_g) })}
        </SavedLine>
      )}
      <SumCard
        rows={[
          { label: tr('process.v1.issue.lotsLabel'), value: <b>{lotCodes(state, task)}</b> },
          {
            label: tr('process.v1.issue.runLabel'),
            value: (
              <b>
                {`…${tail2(task.run_code)}`}
                {task.step_code ? ` · ${stepLabel(state, task.step_code)}` : ''}
              </b>
            ),
          },
          'rule',
          {
            label: tr('process.v1.issue.bagsLabel'),
            value: (
              <b style={{ color: missing.length === 0 ? C.ok : C.ink }}>
                {tr('process.v1.issue.count', {
                  n: task.issued.size,
                  m: task.container_ids.length,
                })}
              </b>
            ),
          },
          {
            label: tr('process.v1.issue.kgSoFar'),
            value: <b>{tr('process.v1.kg', { kg: kg(issuedG) })}</b>,
          },
        ]}
      />
      {moistureMissing && <Warn>{tr('process.v1.issue.moistureMissing')}</Warn>}
      {missing.length > 0 ? (
        <PScanner
          state={state}
          kind="BAG"
          hint={tr('process.v1.issue.scan')}
          paused={confirmDone}
          onCode={(code, entry) => {
            const v = checkIssueBag(state, task, code)
            if (!v.ok) return refusalText(v.reason)
            setSaved(null)
            setErr(null)
            scale.reset()
            setPending({ bag: v.value, qr: code.trim().toUpperCase(), entry })
            return null
          }}
        />
      ) : (
        <Strip icon="ok" iconBox>
          {tr('process.v1.issue.allIn')}
        </Strip>
      )}
      <Hint icon="rowbags">{tr('process.v1.issue.carryHint')}</Hint>
    </V1Screen>
  )
}
